import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Building2, Mail, Phone, Edit, Trash2, Settings2, DollarSign, AlertCircle, CheckCircle2, Calendar, History, Share2, Ban } from 'lucide-react';
import { formatSubStatus } from '@/utils/formatters';
import StatusHistoryModal from '@/components/StatusHistoryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const BAIXA_ITEMS_LABELS = {
  serasa: 'SERASA',
  spc_brasil: 'SPC Brasil',
  boa_vista: 'SCPC Boa Vista',
  ieptb_nacional: 'IEPTB Nacional',
  ieptb_sp: 'IEPTB SP'
};

const PAYMENT_STATUS_CONFIG = {
  pago: { label: 'Pago', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  nao_pago: { label: 'Não Pago', color: 'bg-red-100 text-red-700 border-red-200', icon: DollarSign }
};

export const ClientCard = ({ 
  client, 
  index, 
  isAdmin = false, 
  showStatus = true, 
  showCheckbox = false, 
  isSelected = false,
  onSelect = () => {},
  onClick = () => {},
  onEdit = null,
  onDelete = null,
  onManageStatus = null,
  onPaymentStatusChange = null
}) => {
  const [isChangingPayment, setIsChangingPayment] = useState(false);
  const isPessoaFisica = client.tipo_pessoa === 'fisica';
  const displayName = isPessoaFisica 
    ? (client.nome_completo || 'Nome não informado')
    : (client.razao_social || 'Razão social não informada');
  const displayDoc = isPessoaFisica ? client.cpf : client.cnpj;

  const getStatusLabel = (status) => {
    const labels = {
      protocolo: 'Protocolo',
      em_processo_baixa: 'Em Processo de Baixa',
      concluido: 'Concluído',
      revogada: 'Revogada',
      cancelado: 'Cancelado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      protocolo: 'bg-amber-100 text-amber-700 border-amber-200',
      em_processo_baixa: 'bg-blue-100 text-blue-700 border-blue-200',
      concluido: 'bg-green-100 text-green-700 border-green-200',
      revogada: 'bg-red-100 text-red-700 border-red-200',
      cancelado: 'bg-gray-200 text-gray-700 border-gray-300'
    };
    return colors[status] || 'bg-muted text-muted-foreground';
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(client);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(client.id);
  };

  const handleManageStatus = (e) => {
    e.stopPropagation();
    if (onManageStatus) onManageStatus(client);
  };

  const handleCheckboxChange = (checked) => {
    onSelect(client.id);
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
  };

  const renderBaixaItems = () => {
    if (!client.baixa_items) return null;
    const items = Object.entries(client.baixa_items).filter(([key, value]) => value);
    if (items.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {items.map(([key, value]) => (
          <Badge 
            key={key} 
            variant="outline" 
            className={`text-[10px] px-1.5 py-0 ${value === 'baixado' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}
          >
            {BAIXA_ITEMS_LABELS[key]}: {value === 'baixado' ? '✓' : '...'}
          </Badge>
        ))}
      </div>
    );
  };

  const paymentStatus = client.payment_status || 'nao_pago';
  const paymentConfig = PAYMENT_STATUS_CONFIG[paymentStatus] || PAYMENT_STATUS_CONFIG.nao_pago;
  const PaymentIcon = paymentConfig.icon;

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const clientName = client.nome_completo || client.razao_social || 'Cliente';

  const handleShareLink = async (e) => {
    e.stopPropagation();
    setIsGeneratingLink(true);
    try {
      const response = await axios.get(`${API}/clients/${client.id}/portal-link`, getAuthHeaders());
      const fullUrl = `${window.location.origin}/consulta/${response.data.access_token}`;
      
      // Tentar copiar para clipboard
      try {
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Link copiado! Compartilhe com o cliente.');
      } catch (clipboardError) {
        // Fallback: mostrar o link em um prompt
        toast.success(
          <div className="space-y-2">
            <p>Link gerado! Copie abaixo:</p>
            <code className="text-xs bg-muted p-1 rounded block break-all">{fullUrl}</code>
          </div>,
          { duration: 10000 }
        );
      }
    } catch (error) {
      console.error('Erro ao gerar link:', error);
      toast.error('Erro ao gerar link de acompanhamento');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      onClick={isAdmin ? onClick : undefined}
      className={`border border-border rounded-lg p-5 bg-card ${isAdmin ? 'card-hover cursor-pointer' : ''} ${isSelected ? 'ring-2 ring-primary' : ''}`}
      data-testid={`client-card-${index}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          {showCheckbox && (
            <div onClick={handleCheckboxClick}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={handleCheckboxChange}
                data-testid={`client-checkbox-${index}`}
                className="mt-1"
              />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className="text-xs" data-testid={`client-type-badge-${index}`}>
                {isPessoaFisica ? (
                  <><User className="w-3 h-3 mr-1" />PF</>
                ) : (
                  <><Building2 className="w-3 h-3 mr-1" />PJ</>
                )}
              </Badge>
              {isAdmin && onPaymentStatusChange && (
                <div onClick={(e) => e.stopPropagation()} className="relative">
                  <Select 
                    value={paymentStatus} 
                    onValueChange={(value) => {
                      setIsChangingPayment(true);
                      onPaymentStatusChange(client.id, value).finally(() => setIsChangingPayment(false));
                    }}
                    disabled={isChangingPayment}
                  >
                    <SelectTrigger 
                      className={`h-7 w-auto px-2 text-xs border ${paymentConfig.color} ${isChangingPayment ? 'opacity-50' : ''}`}
                      data-testid={`client-payment-select-${index}`}
                    >
                      <DollarSign className="w-3 h-3 mr-1" />
                      <span>{paymentConfig.label}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pago" className="text-xs">
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3 text-green-600" /> Pago
                        </span>
                      </SelectItem>
                      <SelectItem value="nao_pago" className="text-xs">
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3 text-red-600" /> Não Pago
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Badge de pagamento para visualização (admin sem callback ou parceiro) */}
              {((!isAdmin) || (isAdmin && !onPaymentStatusChange)) && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${paymentConfig.color}`}
                  data-testid={`client-payment-badge-${index}`}
                >
                  <PaymentIcon className="w-3 h-3 mr-1" />
                  {paymentConfig.label}
                </Badge>
              )}
              {isAdmin && (client.partner_name || client.partner_email) && (
                <span className="text-xs text-muted-foreground" data-testid={`client-partner-${index}`}>
                  Por: {client.partner_name || client.partner_email}
                </span>
              )}
              {client.period_name && (
                <Badge variant="secondary" className="text-xs">
                  {client.period_name}
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-lg tracking-tight" data-testid={`client-card-name-${index}`}>
              {displayName}
            </h3>
            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
              {isPessoaFisica ? (
                <User className="w-3 h-3" />
              ) : (
                <Building2 className="w-3 h-3" />
              )}
              <span data-testid={`client-card-doc-${index}`}>{displayDoc}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          {isAdmin && onManageStatus && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleManageStatus}
              className="btn-transition text-primary hover:bg-primary/10"
              title="Gerenciar Status"
              data-testid={`client-status-button-${index}`}
            >
              <Settings2 className="w-4 h-4" />
            </Button>
          )}
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleEdit}
              className="btn-transition"
              data-testid={`client-edit-button-${index}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}
          {/* Botão de histórico - disponível para todos */}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); setShowHistoryModal(true); }}
            className="btn-transition text-muted-foreground hover:text-primary"
            title="Histórico de alterações"
            data-testid={`client-history-button-${index}`}
          >
            <History className="w-4 h-4" />
          </Button>
          {/* Botão de compartilhar link - disponível para todos */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShareLink}
            disabled={isGeneratingLink}
            className="btn-transition text-muted-foreground hover:text-green-600"
            title="Copiar link de acompanhamento"
            data-testid={`client-share-button-${index}`}
          >
            <Share2 className={`w-4 h-4 ${isGeneratingLink ? 'animate-pulse' : ''}`} />
          </Button>
          {isAdmin && onDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              className="btn-transition text-destructive hover:bg-destructive/10"
              data-testid={`client-delete-button-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {client.email && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="w-4 h-4" />
            <span className="truncate" data-testid={`client-card-email-${index}`}>{client.email}</span>
          </div>
        )}
        {client.telefone && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Phone className="w-4 h-4" />
            <span data-testid={`client-card-phone-${index}`}>{client.telefone}</span>
          </div>
        )}
        {client.created_at && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span data-testid={`client-card-date-${index}`}>
              Cadastro: {new Date(client.created_at).toLocaleDateString('pt-BR')} às {new Date(client.created_at).toLocaleTimeString('pt-BR')}
            </span>
          </div>
        )}
        
        {/* Status section with details */}
        {showStatus && client.status && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(client.status)}`} data-testid={`client-status-badge-${index}`}>
                {getStatusLabel(client.status)}
              </span>
              {client.sub_status && (
                <span className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(client.status)}`}>
                  {formatSubStatus(client.sub_status)}
                </span>
              )}
            </div>
            
            {client.status === 'em_processo_baixa' && renderBaixaItems()}
            
            {client.status === 'concluido' && client.conclusao_texto && (
              <p className="text-xs text-muted-foreground mt-2 italic">
                &ldquo;{client.conclusao_texto}&rdquo;
              </p>
            )}

            {client.status === 'revogada' && (
              <p className="text-xs text-red-600 mt-2 font-medium">
                Processo revogado
              </p>
            )}

            {client.status === 'cancelado' && (
              <p className="text-xs text-gray-600 mt-2 font-medium flex items-center gap-1">
                <Ban className="w-3 h-3" />
                Processo cancelado
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>

    {/* Modal de Histórico */}
    <StatusHistoryModal
      isOpen={showHistoryModal}
      onClose={() => setShowHistoryModal(false)}
      clientId={client.id}
      clientName={clientName}
    />
    </>
  );
};

export default ClientCard;
